import { normalizarNombreDocente } from "./docenteNombre";

const ROLES_DOCENTE = ["director", "tutor", "cotutor", "evaluador", "evaluador1", "evaluador2"];
const ROLES_CONTABILIZADOS = new Set(["director", "evaluador"]);
export const ESTADOS_CONTRATACION = ["CONTRATADO", "EN PROCESO DE CONTRATACION"];
const CAMPOS_CONTRATACION = {
  director: "estado_contratacion_director",
  evaluador: "estado_contratacion_evaluador"
};

const normalizarNombre = (nombre) => normalizarNombreDocente(nombre);
const normalizarEstadoContratacion = (estado) => String(estado ?? "").trim().toUpperCase();
const normalizarTextoClave = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
const esExamenComplexivo = (item) => normalizarTextoClave(item?.examen) === "SI";

const obtenerEtiquetaRol = (rol) => {
  const etiquetas = {
    director: "Tutor",
    tutor: "Tutor 2",
    cotutor: "Cotutor",
    evaluador: "Evaluador",
    evaluador1: "Evaluador 1",
    evaluador2: "Evaluador 2"
  };

  return etiquetas[rol] || rol;
};

export const rolTieneContratacion = (rol) => Boolean(CAMPOS_CONTRATACION[rol]);

export const obtenerEstadoContratacionPorRol = (item, rol) => {
  if (!rolTieneContratacion(rol)) {
    return "";
  }

  return normalizarEstadoContratacion(item?.[CAMPOS_CONTRATACION[rol]]);
};

const construirClaveParticipacion = (item, rol, docenteNormalizado) => {
  const maestria = normalizarTextoClave(item?.maestria);
  const tema = normalizarTextoClave(item?.tema);
  const director = normalizarNombre(item?.director);
  const tutor = normalizarNombre(item?.tutor);
  const cotutor = normalizarNombre(item?.cotutor);
  const evaluador = normalizarNombre(item?.evaluador);
  const evaluador1 = normalizarNombre(item?.evaluador1);
  const evaluador2 = normalizarNombre(item?.evaluador2);
  const estudiante = normalizarTextoClave(item?.estudiante);

  if (tema) {
    return [
      docenteNormalizado,
      rol,
      maestria,
      tema,
      director,
      tutor,
      cotutor,
      evaluador,
      evaluador1,
      evaluador2
    ].join("|");
  }

  return [docenteNormalizado, rol, maestria, estudiante].join("|");
};

export const analizarSeleccionado = (data, nombre) => {
  const historial = obtenerHistorialDocente(data, nombre);
  const resultado = {
    director: 0,
    tutor: 0,
    cotutor: 0,
    evaluador: 0,
    evaluador1: 0,
    evaluador2: 0
  };

  historial.forEach((participacion) => {
    resultado[participacion.rol]++;
  });
  return resultado;
};

export const obtenerHistorialDocente = (data, nombre) => {
  const nombreNormalizado = normalizarNombre(nombre);
  const historialMap = new Map();

  data.forEach((item) => {
    if (esExamenComplexivo(item)) {
      return;
    }

    ROLES_DOCENTE.forEach((rol) => {
      if (normalizarNombre(item?.[rol]) !== nombreNormalizado) {
        return;
      }

      const clave = construirClaveParticipacion(item, rol, nombreNormalizado);

      if (!historialMap.has(clave)) {
        historialMap.set(clave, {
          rol,
          rolLabel: obtenerEtiquetaRol(rol),
          estudiantes: new Set(),
          maestria: item.maestria || "Sin maestria",
          cohorte: item.cohorte || "Sin cohorte",
          estado: item.estado || "Sin estado",
          tema: item.tema || "",
          estadoContratacion: obtenerEstadoContratacionPorRol(item, rol),
          fechaTentativa: item.fecha_tentativa || "",
          fechaGraduacion: item.fecha_graduacion || ""
        });
      }

      const entrada = historialMap.get(clave);
      const estudiante = String(item.estudiante || "").trim();

      if (estudiante) {
        entrada.estudiantes.add(estudiante);
      }
    });
  });

  return Array.from(historialMap.values()).map((item) => ({
    ...item,
    estudiante: item.estudiantes.size > 0 ? Array.from(item.estudiantes).join(" / ") : "Sin estudiante"
  }));
};

export const calcularCarga = (data) => {
  const conteo = {};
  const participacionesVistas = new Set();

  data.forEach((item) => {
    if (esExamenComplexivo(item)) {
      return;
    }

    ROLES_DOCENTE.forEach((rol) => {
      const persona = item?.[rol]?.trim();

      if (!persona) {
        return;
      }

      const clave = normalizarNombre(persona);
      const claveParticipacion = construirClaveParticipacion(item, rol, clave);

      if (participacionesVistas.has(claveParticipacion)) {
        return;
      }

      participacionesVistas.add(claveParticipacion);

      if (!conteo[clave]) {
        conteo[clave] = {
          nombre: clave,
          total: 0,
          director: 0,
          tutor: 0,
          cotutor: 0,
          evaluador: 0,
          evaluador1: 0,
          evaluador2: 0,
          directorContratado: 0,
          directorEnProceso: 0,
          directorSinRegistro: 0,
          evaluacionContratado: 0,
          evaluacionEnProceso: 0,
          evaluacionSinRegistro: 0
        };
      }

      conteo[clave][rol]++;

      if (ROLES_CONTABILIZADOS.has(rol)) {
        conteo[clave].total++;
      }

      if (rolTieneContratacion(rol)) {
        const estadoContratacion = obtenerEstadoContratacionPorRol(item, rol);
        const prefijo = rol === "director" ? "director" : "evaluacion";

        if (estadoContratacion === "CONTRATADO") {
          conteo[clave][`${prefijo}Contratado`]++;
        } else if (estadoContratacion === "EN PROCESO DE CONTRATACION") {
          conteo[clave][`${prefijo}EnProceso`]++;
        } else {
          conteo[clave][`${prefijo}SinRegistro`]++;
        }
      }
    });
  });

  return Object.values(conteo).map((docente) => ({
    ...docente,
    participacionesPrincipales: docente.director + docente.evaluador,
    evaluacionesComplementarias: 0,
    participacionesConContratacion:
      docente.directorContratado +
      docente.directorEnProceso +
      docente.evaluacionContratado +
      docente.evaluacionEnProceso
  }));
};

export const obtenerDocentesDisponibles = (data) => {
  const roles = ["director", "tutor", "cotutor", "evaluador", "evaluador1", "evaluador2"];
  const docentes = new Set();

  data.forEach((item) => {
    roles.forEach((rol) => {
      const nombre = item?.[rol]?.trim();

      if (nombre) {
        docentes.add(nombre.toUpperCase());
      }
    });
  });

  return Array.from(docentes).sort((a, b) => a.localeCompare(b, "es"));
};
